<script setup lang="ts">
import ModalCompleteProfile from '@/components/modal/CompleteProfile.vue'
import ModalNewsletterSuccess from '@/components/modal/ModalNewsletterSuccess.vue'
import ModalVerifyProfile from '@/components/modal/VerifyProfile.vue'
import ScrollToTop from '@core/components/ScrollToTop.vue'
import initCore from '@core/initCore'
import { initConfigStore, useConfigStore } from '@core/stores/config'
import { hexToRgb } from '@layouts/utils'
import { storeToRefs } from 'pinia'
import { useTheme } from 'vuetify'
import { useModalStore } from '~/stores/modal'
import { useToastStore } from '~/stores/toast'

const { visible, message, variant } = storeToRefs(useToastStore())
const { isDialogVerifyProfile, isDialogCompleteProfile } = storeToRefs(
    useModalStore()
)
const { global } = useTheme()
const { public: { gtmId } } = useRuntimeConfig()

import { VNodeRenderer } from '@layouts/components/VNodeRenderer'
import { themeConfig } from '@themeConfig'
// ℹ️ Sync current theme with initial loader theme
initCore()
initConfigStore()

const configStore = useConfigStore()
const { isMobile } = useDevice()
if (isMobile) {
    configStore.appContentLayoutNav = 'vertical'
}

const loading = ref(true)
const nuxtApp = useNuxtApp()

nuxtApp.hook('page:start', () => {
    loading.value = true
})
nuxtApp.hook('page:finish', () => {
    loading.value = false
})

const i18nHead = useLocaleHead({
    addDirAttribute: true,
    identifierAttribute: 'id',
    addSeoAttributes: true
})

useHead({
    titleTemplate: (titleChunk) => {
        return titleChunk
            ? `${titleChunk} | Samurai Rekrutmen`
            : 'Samurai Rekrutmen'
    },
    link: [...(i18nHead.value.link || [])],
    meta: [
        ...(i18nHead.value.meta || []),
        { name: 'robots', content: 'index, follow' },
        { name: 'googlebot', content: 'index, follow' },
        { name: 'google-site-verification', content: 'WGbsaTaoLVE2NP5klsktbyeD_65BsZ-VgwRY-5MhToI' }
    ],
    htmlAttrs: {
        lang: i18nHead.value.htmlAttrs!.lang
    }
})

const router = useRouter()
const { $api } = useNuxtApp()
const { setUser, resetUser } = useAuthStore()

let authChannel: BroadcastChannel;

onMounted(() => {
  authChannel = new BroadcastChannel('auth_sync')
  
  authChannel.onmessage = async (event) => {
    if (event.data === 'login_success') {
      const { data: profileData, error } = await $api.auth.getProfile()
      if (!error.value && profileData.value) {
        setUser(profileData.value.data)
        if (router.currentRoute.value.path !== '/dashboard') {
          router.push('/dashboard')
        }
      }
    }
    
    if (event.data === 'logout_success') {
      resetUser()
      if (router.currentRoute.value.path !== '/login') {
        router.push('/login')
      }
    }
  }
})

onUnmounted(() => {
  if (authChannel) authChannel.close()
})
</script>

<template>
    <Html :lang="i18nHead.htmlAttrs.lang" :dir="i18nHead.htmlAttrs.dir">
        <div v-if="loading" class="loading-page">
            <div class="d-flex gap-x-3 align-center">
                <VNodeRenderer :nodes="themeConfig.app.logo" />
            </div>
            <div class="loading">
                <div class="effect-1 effects"></div>
                <div class="effect-2 effects"></div>
                <div class="effect-3 effects"></div>
            </div>
        </div>
        <noscript v-if="gtmId">
            <iframe
                :src="`https://www.googletagmanager.com/ns.html?id=${gtmId}`"
                height="0"
                width="0"
                style="display:none;visibility:hidden"
            />
        </noscript>
        <VLocaleProvider :rtl="configStore.isAppRTL">
            <!-- ℹ️ This is required to set the background color of active nav link based on currently active global theme's primary -->
            <VApp
                :style="`--v-global-theme-primary: ${hexToRgb(
                    global.current.value.colors.primary
                )}`"
            >
                <!-- <NuxtLoadingIndicator
                :color="global.current.value.colors.primary"
            ></NuxtLoadingIndicator> -->
                <NuxtLayout>
                    <NuxtPage />
                </NuxtLayout>

                <ScrollToTop />
                <VSnackbar
                    v-model="visible"
                    location="top"
                    multiline
                    transition="fade-transition"
                >
                    <div class="d-flex text-body-1 text-surface">
                        <VIcon
                            v-if="variant === 'success'"
                            icon="ri-checkbox-circle-fill"
                            color="success"
                            class="me-2"
                            size="25"
                        ></VIcon>
                        <VIcon
                            v-if="variant === 'error'"
                            icon="ri-error-warning-fill"
                            color="error"
                            class="me-2"
                            size="25"
                        ></VIcon
                        ><span>{{ message }}</span>
                    </div>
                </VSnackbar>
                <ModalVerifyProfile v-model="isDialogVerifyProfile" />
                <ModalCompleteProfile v-model="isDialogCompleteProfile" />
                <ModalNewsletterSuccess />
            </VApp>
        </VLocaleProvider>
    </Html>
</template>

<style lang="scss">
.loading-page {
    background-color: white;
    z-index: 999999;
    top: 0;
    left: 0;
    position: fixed;
    width: 100%;
    height: 100vh;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
}

.loading {
    position: relative;
    box-sizing: border-box;
    border: 3px solid transparent;
    border-radius: 50%;
    block-size: 55px;
    inline-size: 55px;
}

.loading .effect-1,
.loading .effect-2,
.loading .effect-3 {
    position: absolute;
    box-sizing: border-box;
    border: 3px solid transparent;
    border-radius: 50%;
    block-size: 100%;
    border-inline-start: 3px solid #000000;
    inline-size: 100%;
}

.loading .effect-1 {
    animation: rotate 1s ease infinite;
}

.loading .effect-2 {
    animation: rotate-opacity 1s ease infinite 0.1s;
}

.loading .effect-3 {
    animation: rotate-opacity 1s ease infinite 0.2s;
}

.loading .effects {
    transition: all 0.3s ease;
}

@keyframes rotate {
    0% {
        transform: rotate(0deg);
    }

    100% {
        transform: rotate(1turn);
    }
}

@keyframes rotate-opacity {
    0% {
        opacity: 0.1;
        transform: rotate(0deg);
    }

    100% {
        opacity: 1;
        transform: rotate(1turn);
    }
}
</style>
